import { watch } from "vue";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import { useAuthSessionStore } from "../stores/authSession";
import router, { resetRouterAccountContext } from "../router";
import api from "../api";
import { logError } from "../helpers/logError";

// On org switch, redirect off any route flagged `meta.orgScoped` to its
// `meta.orgListPath`. List/public routes (no flag) stay where they are; their
// own watchers refetch in-place.
//
// The contract: every route that pins an entity by id MUST set
// `meta.orgScoped: true` and `meta.orgListPath: "/parent"`. The
// `routeGuards.spec.ts` "every :id route is orgScoped" test fails CI when a
// new route forgets — so this guard never silently skips a real detail page.
function getSafeRedirectForOrgChange(currentRoute: RouteLocationNormalizedLoaded): string | null {
	if (currentRoute.meta?.public) {
		return null;
	}
	// Vue Router rolls a child route's meta up to include the parent's, so a
	// /settings/<section> route inherits orgScoped from /settings.
	if (!currentRoute.meta?.orgScoped) {
		return null;
	}
	const listPath = currentRoute.meta?.orgListPath;
	return typeof listPath === "string" && listPath.length > 0 ? listPath : "/";
}

export function useOrgRouteGuard(): { cleanup: () => void } {
	const authSession = useAuthSessionStore();

	const stop = watch(
		() => authSession.organizationId,
		(newOrgId, oldOrgId) => {
			if (!oldOrgId || !newOrgId || newOrgId === oldOrgId) {
				return;
			}

			try {
				api.invalidateMe();
			} catch (err) {
				// Don't let an in-memory cache reset failure block the redirect
				// — the new page will refetch via getMe() either way.
				logError("useOrgRouteGuard: api.invalidateMe failed", err);
			}
			try {
				resetRouterAccountContext();
			} catch (err) {
				logError("useOrgRouteGuard: resetRouterAccountContext failed", err);
			}

			const route = router.currentRoute.value;
			const target = getSafeRedirectForOrgChange(route);
			// Skip the redirect when target === current path. This is reachable
			// via the /settings tile grid (orgListPath: "/settings", route path
			// /settings) — the watch would otherwise post a NavigationDuplicated
			// every time the user switches org from the tile grid. The
			// invalidate-me above already triggered a refetch, so staying put
			// is correct.
			if (target && target !== route.path && target !== route.fullPath) {
				router.replace(target).catch((err) => {
					logError("useOrgRouteGuard: router.replace failed", err, { target });
				});
			}
		}
	);

	return {
		cleanup: () => stop()
	};
}
